import VoipAPI from '../../api/voip';
import sipService from '../../services/sipService';

const state = {
  activeCall: null,
  callHistory: [],
  extensions: [],
  sipState: 'disconnected', // disconnected, connected, registered, failed
  isMuted: false,
  isOnHold: false,
  uiFlags: {
    isInitiating: false,
    isFetchingHistory: false,
    isFetchingExtensions: false,
    isRegistering: false,
  },
};

const getters = {
  getActiveCall: $state => $state.activeCall,
  getCallHistory: $state => $state.callHistory,
  getExtensions: $state => $state.extensions,
  getUIFlags: $state => $state.uiFlags,
  getSipState: $state => $state.sipState,
  hasActiveCall: $state => $state.activeCall !== null,
  isRegistered: $state => $state.sipState === 'registered',
  isMuted: $state => $state.isMuted,
  isOnHold: $state => $state.isOnHold,
};

const mutations = {
  SET_ACTIVE_CALL($state, call) {
    $state.activeCall = call;
  },
  UPDATE_ACTIVE_CALL($state, data) {
    if ($state.activeCall) {
      $state.activeCall = { ...$state.activeCall, ...data };
    }
  },
  CLEAR_ACTIVE_CALL($state) {
    // Só limpa chamadas já encerradas. Impede que o setTimeout de uma chamada
    // ANTERIOR apague o widget de uma chamada NOVA em andamento (bug do widget
    // sumindo ao ligar várias vezes seguidas).
    if (
      $state.activeCall &&
      !['ended', 'failed'].includes($state.activeCall.state)
    ) {
      return;
    }
    $state.activeCall = null;
    $state.isMuted = false;
    $state.isOnHold = false;
  },
  SET_CALL_HISTORY($state, calls) {
    $state.callHistory = calls;
  },
  SET_EXTENSIONS($state, extensions) {
    $state.extensions = extensions;
  },
  SET_SIP_STATE($state, sipState) {
    $state.sipState = sipState;
  },
  SET_MUTED($state, muted) {
    $state.isMuted = muted;
  },
  SET_ON_HOLD($state, onHold) {
    $state.isOnHold = onHold;
  },
  SET_UI_FLAG($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
  APPEND_STAGE_ITEMS() {
    // no-op, needed for kanban compatibility
  },
};

const actions = {
  // Register SIP with API4COM credentials
  async registerSip({ commit }, { ramal, senha }) {
    commit('SET_UI_FLAG', { isRegistering: true });
    try {
      await sipService.register(ramal, senha);
    } catch (error) {
      commit('SET_SIP_STATE', 'failed');
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isRegistering: false });
    }
  },

  // Auto-register using extension credentials from API
  async autoRegister({ commit, state, rootGetters }) {
    // Skip if já registrado ou registrando — re-registrar chama ua.stop() e
    // derruba o WebSocket no meio da ligação (causa do atraso de 45s).
    if (sipService.isRegistered() || state.uiFlags.isRegistering) return;
    commit('SET_UI_FLAG', { isRegistering: true });
    try {
      const { data } = await VoipAPI.getExtensions();
      commit('SET_EXTENSIONS', data);

      // Match extension by current user's email
      const currentUser = rootGetters['auth/getCurrentUser'] || {};
      const userEmail = currentUser.email || '';
      let ext = data.find(e => e.email_address === userEmail);

      // Fallback: try matching by name
      if (!ext && currentUser.name) {
        const nameLower = currentUser.name.toLowerCase();
        ext = data.find(e => (e.first_name || '').toLowerCase().includes(nameLower));
      }

      // Last fallback: use last extension (most likely the newest/personal one)
      if (!ext) ext = data[data.length - 1];

      if (ext) {
        await sipService.register(ext.ramal, ext.senha);
      }
    } catch (error) {
      // Silent fail — VoIP is optional
    } finally {
      commit('SET_UI_FLAG', { isRegistering: false });
    }
  },

  // Initiate a call via SIP (browser-to-PSTN)
  async initiateCall({ commit }, { contactId, contactName, contactPhone, opportunityId }) {
    commit('SET_UI_FLAG', { isInitiating: true });

    // Normalize phone number
    let phone = (contactPhone || '').replace(/\D/g, '');
    if (phone.startsWith('55') && phone.length > 11) {
      phone = '0' + phone.substring(2);
    } else if (phone.startsWith('+55')) {
      phone = '0' + phone.substring(3);
    }

    try {
      sipService.makeCall(phone, { contactId, contactName, contactPhone, opportunityId });

      // Also log to backend for CRM tracking
      try {
        await VoipAPI.initiate({ contactId, opportunityId });
      } catch (e) {
        // Backend logging is best-effort, don't block the call
      }
    } catch (error) {
      commit('SET_ACTIVE_CALL', {
        contact_name: contactName,
        contact_phone: contactPhone,
        state: 'failed',
        error: error.message,
      });
      setTimeout(() => commit('CLEAR_ACTIVE_CALL'), 5000);
    } finally {
      commit('SET_UI_FLAG', { isInitiating: false });
    }
  },

  hangupCall({ commit }) {
    sipService.hangup();
    if (state.activeCall) {
      commit('UPDATE_ACTIVE_CALL', { state: 'ended' });
      setTimeout(() => commit('CLEAR_ACTIVE_CALL'), 3000);
    }
  },

  toggleMute({ commit }) {
    const muted = sipService.toggleMute();
    commit('SET_MUTED', muted);
  },

  toggleHold({ commit }) {
    const held = sipService.toggleHold();
    commit('SET_ON_HOLD', held);
  },

  sendDTMF(_, tone) {
    sipService.sendDTMF(tone);
  },

  async fetchExtensions({ commit }) {
    commit('SET_UI_FLAG', { isFetchingExtensions: true });
    try {
      const { data } = await VoipAPI.getExtensions();
      commit('SET_EXTENSIONS', data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingExtensions: false });
    }
  },

  async fetchCallHistory({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetchingHistory: true });
    try {
      const { data } = await VoipAPI.getCallHistory(params);
      commit('SET_CALL_HISTORY', data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingHistory: false });
    }
  },

  // Handle ActionCable VoIP events
  handleVoipEvent({ commit }, { event, data }) {
    switch (event) {
      case 'voip.call_completed':
        if (state.activeCall) {
          commit('UPDATE_ACTIVE_CALL', {
            duration: data.duration,
            recording_url: data.recording_url,
          });
        }
        break;
      default:
        break;
    }
  },

  disconnectSip() {
    sipService.disconnect();
  },
};

export default { namespaced: true, state, getters, mutations, actions };
